import Close from "@mui/icons-material/Close";
import Menu from "@mui/icons-material/Menu";
import MenuOpen from "@mui/icons-material/MenuOpen";
import { Box, IconButton, Stack } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import { SxProps, useTheme } from "@mui/material/styles";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useFrontChatPresent } from "hooks/useFrontChatPresent";
import { useTenantId } from "hooks/useTenant";
import { Fragment } from "react";
import { NavLink } from "react-router-dom";

import EncampLogo from "../../assets/EncampLogo.svg";
import { NavDrawerScrollSurface } from "./NavDrawerScrollSurface";
import {
  type GroupChildNavItem,
  type GroupNavItem,
  NavItemV2,
  type StandardNavItem,
} from "./NavItemV2";
import { SectionNavRow } from "./SectionNavRow";

export type NavSection = {
  title: string;
  longTitle?: string;
  textColor?: string;
  sx?: SxProps;
  items: NavItem[];
};

export type NavItem = StandardNavItem | GroupChildNavItem | GroupNavItem;

interface NavDrawerProps {
  navSections: NavSection[];
  collapsed: boolean;
  expandedWidth: string;
  collapsedWidth: string;
  open: boolean;
  /** True when the nav should behave like a mobile overlay drawer instead of a desktop sidebar. */
  isMobileDrawerMode: boolean;
  isMobile: boolean;
  onClose: () => void;
  onNavToggle: () => void;
}

// Use stable keys so each nav row keeps the correct persisted expansion state.
function getNavItemKey(navItem: NavItem, idx: number) {
  switch (navItem.kind) {
    case "Group":
      return `group-${navItem.text}-${navItem.path}`;
    case "Standard":
    case "GroupChild":
      return `${navItem.kind}-${navItem.text}-${navItem.path ?? idx}`;
  }
}

function getSectionKey(section: NavSection, idx: number) {
  return `section-${section.title}-${idx}`;
}

export function LeftNavDrawerV2({
  navSections,
  collapsed,
  expandedWidth,
  collapsedWidth,
  open,
  isMobileDrawerMode,
  isMobile,
  onClose,
  onNavToggle,
}: NavDrawerProps) {
  const theme = useTheme();
  // Reserve space at the bottom of the scroll surface when either Scout's
  // launcher or Front's chat bubble would otherwise overlap the last nav
  // items. Both launchers sit at ~bottom: 20 with a 56px button + breathing
  // room, so ~96px clears them.
  const { featureFlags } = useFeatureFlags();
  const scoutEnabled = Boolean(featureFlags[FeatureFlagKey.Scout]);
  const frontPresent = useFrontChatPresent();
  const navBottomPadding = scoutEnabled || frontPresent ? 12 : 0;
  const isCollapsed = isMobileDrawerMode ? false : collapsed;
  const drawerWidth = isMobileDrawerMode
    ? isMobile
      ? "100vw"
      : expandedWidth
    : isCollapsed
      ? collapsedWidth
      : expandedWidth;
  const navToggleLabel = isCollapsed ? "Expand navigation" : "Collapse navigation";
  const drawerVariant = isMobileDrawerMode ? "temporary" : "permanent";
  const closeOnNavigate = isMobileDrawerMode ? onClose : undefined;
  return (
    <Drawer
      variant={drawerVariant}
      open={isMobileDrawerMode ? open : true}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          overflowX: "hidden",
          boxSizing: "border-box",
          borderRight: "none",
          transition: (theme) =>
            theme.transitions.create(["width"], {
              easing: isCollapsed
                ? theme.transitions.easing.sharp
                : theme.transitions.easing.easeOut,
              duration: isCollapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <NavDrawerScrollSurface
        data-testid="left-nav-drawer"
        data-nav-mode={drawerVariant}
        data-drawer-width={drawerWidth}
        sx={{
          width: drawerWidth,
        }}
      >
        <Box component="nav" aria-label="Primary navigation" sx={{ minHeight: "100%" }}>
          {isMobileDrawerMode && isMobile && (
            <IconButton
              aria-label="Close navigation"
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                color: "white",
              }}
            >
              <Close />
            </IconButton>
          )}
          <Stack
            sx={{
              paddingTop: isCollapsed ? theme.spacing(2) : theme.spacing(6),
              transition: (theme) =>
                theme.transitions.create(["padding"], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: isMobileDrawerMode && isMobile ? "flex-start" : "center",
                paddingLeft: isMobileDrawerMode && isMobile ? theme.spacing(7.5) : undefined,
                marginBottom: isMobileDrawerMode && isMobile ? theme.spacing(3) : undefined,
                alignItems: "center",
                paddingBottom: isCollapsed
                  ? theme.spacing(2)
                  : isMobileDrawerMode && !isMobile
                    ? theme.spacing(3)
                    : 0,
              }}
            >
              <NavLinkToRoot>
                <img
                  src={EncampLogo}
                  alt="Encamp logo"
                  style={{
                    width: isCollapsed ? "28px" : "80px",
                    transition: "width 0.1s cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                />
              </NavLinkToRoot>
            </Box>
            {!isMobileDrawerMode && (
              <IconButton
                aria-label={navToggleLabel}
                onClick={onNavToggle}
                sx={{
                  color: "#878D99",
                  width: isCollapsed ? 40 : 44,
                  height: isCollapsed ? 40 : 44,
                  marginX: "auto",
                  marginBottom: isCollapsed ? 1 : 0,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                  },
                }}
              >
                {isCollapsed ? (
                  <Menu sx={{ fontSize: "1.5rem" }} />
                ) : (
                  <MenuOpen sx={{ fontSize: "1.5rem" }} />
                )}
              </IconButton>
            )}
          </Stack>
          <List
            // Reserve room so the last nav items can scroll above the FrontChat or
            // Scout floating launcher. Conditional so staff routes (no launchers)
            // keep their original tight padding.
            sx={{ paddingTop: 0, paddingBottom: navBottomPadding }}
          >
            {navSections.map((section, idx) => (
              <Fragment key={getSectionKey(section, idx)}>
                <SectionNavRow section={section} collapsed={isCollapsed} />
                {section.items.map((item, idx) => (
                  <NavItemV2
                    key={getNavItemKey(item, idx)}
                    navItem={item}
                    collapsed={isCollapsed}
                    onNavigate={closeOnNavigate}
                  />
                ))}
              </Fragment>
            ))}
          </List>
        </Box>
      </NavDrawerScrollSurface>
    </Drawer>
  );
}

const NavLinkToRoot = ({ children }: { children: React.ReactNode }) => {
  const { isStaff } = useCurrentUser();
  const tenantId = useTenantId();

  if (tenantId) {
    return <NavLink to={`/o/${tenantId}`}>{children}</NavLink>;
  }

  if (isStaff) {
    return <NavLink to="/staff">{children}</NavLink>;
  }

  return <NavLink to="/">{children}</NavLink>;
};
